/* global HTMLElement, customElements */
// DLUX live chat widget. Floating bubble that opens a small panel and
// relays messages to the master app at /api/livechat (human replies only,
// no AI). Conversation credentials persist in localStorage so a returning
// visitor sees their thread and any replies sent while they were away.

(function () {
  "use strict";

  if (typeof customElements === "undefined" || customElements.get("dlux-live-chat")) {
    return;
  }

  // Same conservative crawler guard as journey-tracker.js — no point
  // rendering a chat bubble for Googlebot screenshots.
  var BOT_UA_RE =
    /Googlebot|AdsBot-Google|Mediapartners-Google|FeedFetcher-Google|Google-InspectionTool|GoogleOther|Storebot-Google|Chrome-Lighthouse|HeadlessChrome|bingbot|YandexBot|Baiduspider|DuckDuckBot|facebookexternalhit|Twitterbot|LinkedInBot|WhatsApp|TelegramBot|Applebot|Pinterest|Slurp|Sogou|Discordbot|Bytespider|PetalBot|SemrushBot|AhrefsBot|MJ12bot|DotBot/i;
  if (BOT_UA_RE.test(navigator.userAgent || "")) return;

  var LS_ID = "dlux_lc_id";
  var LS_TOKEN = "dlux_lc_token";
  var LS_NAME = "dlux_lc_name";
  var LS_EMAIL = "dlux_lc_email";
  var SS_TEASER_DISMISSED = "dlux_lc_teaser_off";
  var POLL_VISIBLE_MS = 4000;
  var POLL_HIDDEN_MS = 20000;
  var TEASER_DELAY_MS = 0; // show immediately; only ✕ or opening the chat dismisses it
  var OFFLINE_SUB = "We usually reply within a few hours.";
  var ONLINE_SUB = "Online now";
  var CLOSED_SUB = "This chat is closed. Send a message to start again.";

  function lsGet(key) {
    try { return window.localStorage.getItem(key) || ""; } catch (_) { return ""; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key) || ""; } catch (_) { return ""; }
  }
  function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (_) {}
  }

  // "2:34 pm" for today, "6 Jun, 2:34 pm" for older messages.
  function fmtTime(iso) {
    var d = iso ? new Date(iso) : new Date();
    if (isNaN(d.getTime())) return "";
    var t = d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
    if (d.toDateString() === new Date().toDateString()) return t;
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" }) + ", " + t;
  }

  class DluxLiveChat extends HTMLElement {
    constructor() {
      super();
      this.open = false;
      this.busy = false;
      this.lastSeen = null; // ISO of newest message we've rendered
      this.pollTimer = null;
      this.status = "open";
      this.online = false;
    }

    connectedCallback() {
      this.endpoint = this.dataset.endpoint || "";
      if (!this.endpoint) return;

      this.conversationId = lsGet(LS_ID);
      this.token = lsGet(LS_TOKEN);

      this.render();
      this.bind();
      this.fetchStatus();

      // Returning visitor with an existing thread: pull history quietly so
      // the badge can show a reply that arrived while they were away.
      if (this.conversationId && this.token) {
        this.poll(true);
        this.startPolling();
      } else {
        this.maybeShowTeaser();
      }

      document.addEventListener("visibilitychange", () => {
        if (this.conversationId) this.startPolling();
      });
    }

    render() {
      this.innerHTML =
        '<div class="dlux-lc__teaser" data-lc-teaser hidden>' +
        '  <button type="button" class="dlux-lc__teaser-text" data-lc-teaser-open>We are here if you need a hand.</button>' +
        '  <button type="button" class="dlux-lc__teaser-close" aria-label="Dismiss" data-lc-teaser-close>&times;</button>' +
        "</div>" +
        '<button type="button" class="dlux-lc__bubble" aria-label="Chat with us" aria-expanded="false" aria-haspopup="dialog" data-lc-bubble>' +
        '  <svg class="dlux-lc__icon--chat" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>' +
        '  </svg>' +
        '  <svg class="dlux-lc__icon--close" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
        '    <path d="M6 6l12 12M18 6L6 18"/>' +
        '  </svg>' +
        '  <span class="dlux-lc__badge" data-lc-badge hidden></span>' +
        "</button>" +
        '<div class="dlux-lc__panel" role="dialog" aria-label="Chat with DLUX Essentials" data-lc-panel hidden>' +
        '  <div class="dlux-lc__head">' +
        "    <div>" +
        '      <div class="dlux-lc__title">DLUX Essentials</div>' +
        '      <div class="dlux-lc__sub"><span class="dlux-lc__dot" data-lc-dot hidden></span><span data-lc-subtext>We usually reply within a few hours.</span></div>' +
        "    </div>" +
        '    <button type="button" class="dlux-lc__close" aria-label="Close chat" data-lc-close>&times;</button>' +
        "  </div>" +
        '  <div class="dlux-lc__messages" role="log" aria-live="polite" data-lc-messages></div>' +
        '  <div class="dlux-lc__intro" data-lc-intro>' +
        '    <p class="dlux-lc__intro-text">Hi! Ask us anything about our products, your order, or shipping. A real person reads every message.</p>' +
        '    <input type="text" class="dlux-lc__input" data-lc-name placeholder="Your name (optional)" maxlength="80" autocomplete="name">' +
        '    <input type="email" class="dlux-lc__input" data-lc-email placeholder="Email, so we can reply if you step away" maxlength="254" autocomplete="email">' +
        "  </div>" +
        '  <form class="dlux-lc__composer" data-lc-form>' +
        '    <textarea class="dlux-lc__textarea" data-lc-text rows="2" maxlength="2000" placeholder="Type your message" required></textarea>' +
        '    <button type="submit" class="dlux-lc__send" data-lc-send>Send</button>' +
        "  </form>" +
        '  <div class="dlux-lc__note" data-lc-note>Or email us anytime at contact@dlux-essentials.com' +
        '    <button type="button" class="dlux-lc__end" data-lc-end hidden>End chat</button>' +
        "  </div>" +
        "</div>";

      this.bubbleEl = this.querySelector("[data-lc-bubble]");
      this.badgeEl = this.querySelector("[data-lc-badge]");
      this.panelEl = this.querySelector("[data-lc-panel]");
      this.messagesEl = this.querySelector("[data-lc-messages]");
      this.introEl = this.querySelector("[data-lc-intro]");
      this.nameEl = this.querySelector("[data-lc-name]");
      this.emailEl = this.querySelector("[data-lc-email]");
      this.formEl = this.querySelector("[data-lc-form]");
      this.textEl = this.querySelector("[data-lc-text]");
      this.sendEl = this.querySelector("[data-lc-send]");
      this.dotEl = this.querySelector("[data-lc-dot]");
      this.subTextEl = this.querySelector("[data-lc-subtext]");
      this.teaserEl = this.querySelector("[data-lc-teaser]");
      this.endEl = this.querySelector("[data-lc-end]");

      if (this.conversationId) this.endEl.hidden = false;

      this.nameEl.value = lsGet(LS_NAME);
      this.emailEl.value = lsGet(LS_EMAIL);

      if (this.conversationId) this.introEl.hidden = true;
    }

    bind() {
      var self = this;
      this.bubbleEl.addEventListener("click", function () { self.toggle(); });
      this.querySelector("[data-lc-close]").addEventListener("click", function () {
        self.toggle(false);
      });
      this.formEl.addEventListener("submit", function (evt) {
        evt.preventDefault();
        self.submit();
      });
      this.textEl.addEventListener("keydown", function (evt) {
        if (evt.key === "Enter" && !evt.shiftKey) {
          evt.preventDefault();
          self.submit();
        }
      });
      document.addEventListener("keydown", function (evt) {
        if (evt.key === "Escape" && self.open) {
          self.toggle(false);
          self.bubbleEl.focus();
        }
      });
      this.querySelector("[data-lc-teaser-open]").addEventListener("click", function () {
        self.hideTeaser();
        self.toggle(true);
      });
      this.querySelector("[data-lc-teaser-close]").addEventListener("click", function () {
        self.hideTeaser();
      });
      this.endEl.addEventListener("click", function () {
        self.endChat();
      });
    }

    // ── End chat (customer-initiated) ─────────────────────────────────────
    // Two-step to avoid accidental taps: first click arms it, second
    // confirms. Ends the thread server-side, clears local credentials and
    // the on-screen transcript (shared computers), returns to a fresh start.

    endChat() {
      var self = this;
      if (!this.conversationId) return;

      if (!this.endArmed) {
        this.endArmed = true;
        this.endEl.textContent = "End chat? Tap again to confirm";
        setTimeout(function () {
          self.endArmed = false;
          self.endEl.textContent = "End chat";
        }, 4000);
        return;
      }

      // Best-effort server close — local cleanup happens regardless.
      fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          op: "end",
          conversation_id: this.conversationId,
          token: this.token,
        }),
      }).catch(function () {});

      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.conversationId = "";
      this.token = "";
      lsSet(LS_ID, "");
      lsSet(LS_TOKEN, "");
      this.lastSeen = null;
      this.status = "open";
      this.endArmed = false;
      // Keep the transcript on screen so the customer can still read the
      // conversation. It clears when they start a new chat (see submit())
      // or leave the page — credentials are already gone either way.
      this.transcriptEnded = true;
      this.endEl.textContent = "End chat";
      this.endEl.hidden = true;
      this.introEl.hidden = false;
      this.setBadge(0);
      this.refreshSub();
      this.appendSystem("Chat ended. Thanks for stopping by.");
    }

    // ── Teaser (proactive nudge, desktop only via CSS) ────────────────────

    maybeShowTeaser() {
      if (ssGet(SS_TEASER_DISMISSED)) return;
      var self = this;
      setTimeout(function () {
        if (self.open || self.conversationId || ssGet(SS_TEASER_DISMISSED)) return;
        self.teaserEl.hidden = false;
      }, TEASER_DELAY_MS);
    }

    hideTeaser() {
      this.teaserEl.hidden = true;
      ssSet(SS_TEASER_DISMISSED, "1");
    }

    // ── Presence ──────────────────────────────────────────────────────────

    fetchStatus() {
      var self = this;
      fetch(this.endpoint + "?op=status")
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.ok) self.setPresence(!!data.online);
        })
        .catch(function () { /* presence is cosmetic — fail quiet */ });
    }

    setPresence(online) {
      this.online = online;
      this.refreshSub();
    }

    refreshSub() {
      if (this.status === "closed") {
        this.subTextEl.textContent = CLOSED_SUB;
        this.dotEl.hidden = true;
        return;
      }
      this.subTextEl.textContent = this.online ? ONLINE_SUB : OFFLINE_SUB;
      this.dotEl.hidden = !this.online;
    }

    toggle(force) {
      this.open = typeof force === "boolean" ? force : !this.open;
      this.panelEl.hidden = !this.open;
      this.classList.toggle("is-open", this.open);
      this.bubbleEl.setAttribute("aria-expanded", this.open ? "true" : "false");
      this.bubbleEl.setAttribute(
        "aria-label",
        this.open ? "Close chat" : "Chat with us"
      );
      if (this.open) {
        this.hideTeaser();
        this.setBadge(0);
        this.scrollToEnd();
        this.textEl.focus();
        this.fetchStatus();
        if (this.conversationId) this.poll(false);
      }
    }

    setBadge(n) {
      this.unseen = n;
      if (n > 0) {
        this.badgeEl.textContent = String(n);
        this.badgeEl.hidden = false;
      } else {
        this.badgeEl.hidden = true;
      }
    }

    appendMessage(sender, content, pending, sentAt) {
      var div = document.createElement("div");
      div.className =
        "dlux-lc__msg dlux-lc__msg--" + (sender === "admin" ? "admin" : "customer") +
        (pending ? " is-pending" : "");
      div.textContent = content;
      var time = document.createElement("span");
      time.className = "dlux-lc__msg-time";
      time.textContent = fmtTime(sentAt);
      div.appendChild(time);
      this.messagesEl.appendChild(div);
      this.scrollToEnd();
      return div;
    }

    appendSystem(text) {
      var div = document.createElement("div");
      div.className = "dlux-lc__system";
      div.textContent = text;
      this.messagesEl.appendChild(div);
      this.scrollToEnd();
    }

    scrollToEnd() {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }

    submit() {
      if (this.busy) return;
      var text = (this.textEl.value || "").trim();
      if (!text) return;

      var name = (this.nameEl.value || "").trim();
      var email = (this.emailEl.value || "").trim();
      lsSet(LS_NAME, name);
      lsSet(LS_EMAIL, email);

      this.busy = true;
      this.sendEl.disabled = true;
      // Starting fresh after an ended chat: clear the old transcript now.
      if (!this.conversationId && this.transcriptEnded) {
        this.messagesEl.innerHTML = "";
        this.transcriptEnded = false;
      }
      var pendingEl = this.appendMessage("customer", text, true);
      this.textEl.value = "";

      var self = this;
      var isStart = !this.conversationId;
      var payload = isStart
        ? { op: "start", name: name, email: email, page_path: location.pathname, message: text }
        : {
            op: "message",
            conversation_id: this.conversationId,
            token: this.token,
            name: name,
            email: email,
            message: text,
          };

      fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!data || !data.ok) throw new Error((data && data.error) || "send_failed");
          pendingEl.classList.remove("is-pending");
          if (typeof data.online === "boolean") {
            self.status = "open";
            self.setPresence(data.online);
          }
          if (isStart) {
            self.conversationId = data.conversation_id;
            self.token = data.token;
            lsSet(LS_ID, self.conversationId);
            lsSet(LS_TOKEN, self.token);
            self.introEl.hidden = true;
            self.endEl.hidden = false;
            self.lastSeen = new Date().toISOString();
            self.appendSystem("Thanks for your message. We will reply here as soon as we can" + (email ? ", or by email if you step away." : "."));
            self.startPolling();
          }
        })
        .catch(function (err) {
          pendingEl.classList.add("is-failed");
          self.appendSystem(
            err && err.message === "too_many_requests"
              ? "You are sending messages a little too quickly. Please wait a moment and try again."
              : "That message did not go through. Please try again, or email contact@dlux-essentials.com."
          );
        })
        .then(function () {
          self.busy = false;
          self.sendEl.disabled = false;
        });
    }

    startPolling() {
      var self = this;
      if (this.pollTimer) clearInterval(this.pollTimer);
      var interval = document.visibilityState === "hidden" ? POLL_HIDDEN_MS : POLL_VISIBLE_MS;
      this.pollTimer = setInterval(function () { self.poll(false); }, interval);
    }

    poll(initialLoad) {
      if (!this.conversationId || !this.token || this.polling) return;
      var self = this;
      this.polling = true;

      var params =
        "?op=poll&conversation_id=" + encodeURIComponent(this.conversationId) +
        "&token=" + encodeURIComponent(this.token) +
        (this.lastSeen && !initialLoad ? "&since=" + encodeURIComponent(this.lastSeen) : "");

      fetch(this.endpoint + params)
        .then(function (res) {
          if (res.status === 404) {
            // Conversation no longer exists server-side; reset local state.
            self.conversationId = "";
            self.token = "";
            lsSet(LS_ID, "");
            lsSet(LS_TOKEN, "");
            self.introEl.hidden = false;
            if (self.pollTimer) clearInterval(self.pollTimer);
            return null;
          }
          return res.json();
        })
        .then(function (data) {
          if (!data || !data.ok) return;
          self.status = data.status;
          self.online = !!data.online;
          var newAdmin = 0;
          for (var i = 0; i < data.messages.length; i++) {
            var m = data.messages[i];
            if (initialLoad) {
              self.appendMessage(m.sender, m.content, false, m.sent_at);
            } else if (m.sender === "admin") {
              self.appendMessage("admin", m.content, false, m.sent_at);
              newAdmin++;
            }
            if (!self.lastSeen || m.sent_at > self.lastSeen) self.lastSeen = m.sent_at;
          }
          if (newAdmin > 0 && !self.open) {
            self.setBadge((self.unseen || 0) + newAdmin);
          }
          self.refreshSub();
        })
        .catch(function () { /* network blip — next poll will retry */ })
        .then(function () { self.polling = false; });
    }
  }

  customElements.define("dlux-live-chat", DluxLiveChat);
})();
